// Account achievements on the wire (ACHIEVEMENTS.md §15, BACKLOG A3).
//
// `GET /api/me/achievements` answers `AchievementsView`: what the account has
// found, what it can try, the riddles of what it has not found, and how many
// secrets are left — family by family, the families that belong to no sport
// first and then each sport the person has played under its own name.
//
// A HIDDEN ROW NEVER LEAVES THE SERVER. A hinted achievement goes out as an
// opaque key, an icon and its riddle; a secret as a count. Its id, name and
// condition are in the catalog (`@fsp/achievements`), which no client imports.
//
// A found one is once per account, ever (§9): where it was first earned, the
// moment it was earned with, and whether it was found late — by a backfill,
// for something that happened before the achievement existed (§13).

import { z } from 'zod';

export const AchievementShows = z.enum(['open', 'hinted', 'secret']);
export type AchievementShows = z.infer<typeof AchievementShows>;

export const AchievementTone = z.enum(['glory', 'shame', 'fun']);

/**
 * Where an open achievement is tried: a feature, which a client maps to its
 * own screen ("Open draft settings"). Not a path, because a path is one
 * client's.
 */
export const AchievementFeature = z.enum([
  'create-league',
  'join-league',
  'team-settings',
  'looks',
  'detail-level',
  'install',
  'notifications',
  'league-layout',
  'draft-settings',
  'draft-prep',
  'invite',
  'waivers',
  'trades',
  'rules',
  'chat',
  'lineup',
  'record-book',
  'draft-recap',
  'trophy-room',
]);
export type AchievementFeature = z.infer<typeof AchievementFeature>;

/** An achievement the account has earned. */
export const FoundAchievement = z.object({
  id: z.string(),
  name: z.string(),
  /** An icon id; a client draws a default for one it does not know. */
  icon: z.string(),
  tone: AchievementTone,
  family: z.string(),
  /** How it showed before it was earned. */
  shows: AchievementShows,
  /** What earns it, in plain words: said once it is earned, whatever it showed before. */
  asks: z.string(),
  /** The moment: "Justin Tucker missed 3 kicks in Week 6." */
  line: z.string(),
  /** The riddle a hinted one had, kept after earning ("The hint was: …"). Null for the rest. */
  hint: z.string().nullable(),
  earnedAt: z.string(),
  /** Where it was first earned; null for one about the site itself, or found by a backfill with no one league to name. */
  league: z.object({ id: z.string(), name: z.string() }).nullable(),
  season: z.number().int().nullable(),
  period: z.number().int().nullable(),
  /** Found late, by a backfill, for something that happened before it existed (§13). */
  retro: z.boolean(),
});
export type FoundAchievement = z.infer<typeof FoundAchievement>;

/** An open achievement not found yet: its name, what it asks, and how near it is. */
export const OpenAchievement = z.object({
  id: z.string(),
  name: z.string(),
  icon: z.string(),
  asks: z.string(),
  /** For one that counts: how many of how many. Null for a one-time thing. */
  progress: z.object({ have: z.number().int().min(0), need: z.number().int().min(1) }).nullable(),
  feature: AchievementFeature.nullable(),
});
export type OpenAchievement = z.infer<typeof OpenAchievement>;

/** A hinted achievement not found yet: a riddle, never its name or condition. */
export const HintedAchievement = z.object({
  /** Opaque and stable; it does not spell the achievement's id. */
  key: z.string(),
  icon: z.string(),
  hint: z.string(),
  /** A leaguemate who found it, when its line was posted to a league I am in (§7). Null until then. */
  foundBy: z.object({ name: z.string(), league: z.string() }).nullable(),
});
export type HintedAchievement = z.infer<typeof HintedAchievement>;

export const AchievementFamily = z.object({
  id: z.string(),
  name: z.string(),
  icon: z.string(),
  /** The sport's name, for a family that belongs to one; null for the site's own. */
  sport: z.string().nullable(),
  /** Newest first. */
  found: z.array(FoundAchievement),
  /** Closest to done first. */
  open: z.array(OpenAchievement),
  hints: z.array(HintedAchievement),
  /** How many secrets in it are still to find. */
  secrets: z.number().int().min(0),
});
export type AchievementFamily = z.infer<typeof AchievementFamily>;

export const AchievementsView = z.object({
  summary: z.object({
    found: z.number().int().min(0),
    /** Still to find, of each kind. */
    open: z.number().int().min(0),
    hinted: z.number().int().min(0),
    secret: z.number().int().min(0),
  }),
  /** Found in the last fourteen days, newest first. */
  recent: z.array(FoundAchievement),
  /** The families that belong to no sport first, then each sport the person has played. */
  families: z.array(AchievementFamily),
});
export type AchievementsView = z.infer<typeof AchievementsView>;

/**
 * `POST /api/me/achievements/seen` — these were shown to the person (§12), so
 * the moment shows once on any device: `MeView.unlocked` stops carrying them.
 * Ids the account has not found, or has seen already, are left alone.
 */
export const AchievementsSeenRequest = z.object({
  ids: z.array(z.string().min(1).max(80)).min(1).max(200),
});
export type AchievementsSeenRequest = z.infer<typeof AchievementsSeenRequest>;

/** How many of them were marked now; none when another device got there first. */
export const AchievementsSeen = z.object({ marked: z.number().int().min(0) });
export type AchievementsSeen = z.infer<typeof AchievementsSeen>;

/**
 * A response header an action's answer carries when that action earned the
 * signed-in person something, with how many (§15). It says only "ask
 * `GET /api/me` now": the unlocks themselves are `MeView.unlocked`, one place
 * for every client, so a client that ignores the header still gets them on its
 * next `/api/me`.
 */
export const UNLOCKED_HEADER = 'x-achievements-unlocked';
